import { QueryClient, useQuery } from "@tanstack/react-query";
import { ReviewType } from "../components/constants/mockPosts"; // Adjust path to your ReviewType
import { getClientDomain } from "./useFetchUnits";
import { logEnvironment } from "@components/lib/utils";

export type RawReview = {
  id: number;
  review_type: number;
  user_id: number;
  property_id: number;
  review: string;
  created_at: string;
  updated_at: string;
  thumbs_up_count: number;
  thumbs_down_count: number;
  user_liked: boolean;
  user_disliked: boolean;
  comments_count: number;
  user: {
    id: number;
    name: string;
    tier_id: number;
    created_at: string;
    updated_at: string;
    profile_picture_url: string;
  };
};

export type ApiResponse = {
  status: "success" | "error";
  data: RawReview[];
};

export const transformReviewsResponse = (
  apiResponse: ApiResponse
): ReviewType[] => {
  if (apiResponse?.status !== "success" || !Array.isArray(apiResponse?.data)) {
    return [];
  }
  return apiResponse.data.map(
    (review: RawReview): ReviewType => ({
      id: String(review.id),
      name: review.user?.name || "Anonymous",
      photo: review.user?.profile_picture_url || "/images/default-avatar.png",
      likes: review.thumbs_up_count ?? 0,
      dislikes: review.thumbs_down_count ?? 0,
      text: review.review,
      type:
        review.review_type === 1
          ? "positive"
          : review.review_type === 2
          ? "negative"
          : "neutral",
      date: review.created_at,
      variant: false,
      replies: [],
    })
  );
};

const fetchReviews = async (): Promise<ReviewType[]> => {
  const location = getClientDomain("fetching reveiews");
  logEnvironment("this the fetch review componennt");

  const REVIEWS_ENDPOINT = `${process.env.NEXT_PUBLIC_BASE_URL}api/v1/custom/company-reviews/${location}`;

  const res = await fetch(REVIEWS_ENDPOINT);
  if (!res.ok) {
    throw new Error("Failed to fetch reviews");
  }
  const json: ApiResponse = await res.json();
  return transformReviewsResponse(json);
};

export const useFetchReviews = () => {
  return useQuery<ReviewType[], Error>({
    queryKey: ["companyReviews"],
    queryFn: fetchReviews,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const prefetchReviews = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ["companyReviews"],
    queryFn: fetchReviews,
  });
};
